// src/contexts/UIContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * UIContext
 * 
 * Manages global UI state:
 * - Sidebar open/closed state
 * - Theme (light/dark) - future
 * - Layout preferences
 * - Notification preferences
 * 
 * @example
 * const { sidebarOpen, toggleSidebar } = useUI();
 */

const UIContext = createContext();

export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within UIProvider');
  }
  return context;
};

export const UIProvider = ({ children }) => {
  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Theme state (future)
  const [theme, setTheme] = useState('light'); // 'light' | 'dark'

  // Layout preferences
  const [layoutDensity, setLayoutDensity] = useState('default'); // 'compact' | 'default' | 'comfortable'
  
  // Notification preferences
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024; // lg breakpoint
      setIsMobile(mobile);
      
      // Auto-close sidebar on mobile
      if (mobile) {
        setSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load preferences from localStorage
  useEffect(() => {
    try {
      const savedPreferences = localStorage.getItem('uiPreferences');
      if (savedPreferences) {
        const prefs = JSON.parse(savedPreferences);
        if (prefs.theme) setTheme(prefs.theme);
        if (prefs.layoutDensity) setLayoutDensity(prefs.layoutDensity);
        if (prefs.notificationsEnabled !== undefined) {
          setNotificationsEnabled(prefs.notificationsEnabled);
        }
        if (prefs.soundEnabled !== undefined) {
          setSoundEnabled(prefs.soundEnabled);
        }
        // Don't restore sidebar state on mobile
        if (prefs.sidebarOpen !== undefined && !isMobile) {
          setSidebarOpen(prefs.sidebarOpen);
        }
      }
    } catch (error) {
      console.error('Failed to load UI preferences:', error);
    }
  }, [isMobile]);

  // Save preferences to localStorage
  const savePreferences = useCallback((newPrefs) => {
    try {
      const current = JSON.parse(localStorage.getItem('uiPreferences') || '{}');
      const updated = { ...current, ...newPrefs };
      localStorage.setItem('uiPreferences', JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to save UI preferences:', error);
    }
  }, []);

  // Sidebar functions
  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => {
      const newState = !prev;
      savePreferences({ sidebarOpen: newState });
      return newState;
    });
  }, [savePreferences]);

  const openSidebar = useCallback(() => {
    setSidebarOpen(true);
    savePreferences({ sidebarOpen: true });
  }, [savePreferences]);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
    savePreferences({ sidebarOpen: false });
  }, [savePreferences]);

  // Theme functions
  const toggleTheme = useCallback(() => {
    setTheme(prev => {
      const newTheme = prev === 'light' ? 'dark' : 'light';
      savePreferences({ theme: newTheme });
      
      // Apply theme to document
      if (newTheme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      
      return newTheme;
    });
  }, [savePreferences]);

  const setLightTheme = useCallback(() => {
    setTheme('light');
    document.documentElement.classList.remove('dark');
    savePreferences({ theme: 'light' });
  }, [savePreferences]);

  const setDarkTheme = useCallback(() => {
    setTheme('dark');
    document.documentElement.classList.add('dark');
    savePreferences({ theme: 'dark' });
  }, [savePreferences]);

  // Layout density functions
  const changeLayoutDensity = useCallback((density) => {
    setLayoutDensity(density);
    savePreferences({ layoutDensity: density });
  }, [savePreferences]);

  // Notification functions
  const toggleNotifications = useCallback(() => {
    setNotificationsEnabled(prev => {
      const newState = !prev;
      savePreferences({ notificationsEnabled: newState });
      return newState;
    });
  }, [savePreferences]);

  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => {
      const newState = !prev;
      savePreferences({ soundEnabled: newState });
      return newState;
    });
  }, [savePreferences]);

  // Reset all preferences
  const resetPreferences = useCallback(() => {
    setSidebarOpen(true);
    setTheme('light');
    setLayoutDensity('default');
    setNotificationsEnabled(true);
    setSoundEnabled(true);
    localStorage.removeItem('uiPreferences');
    document.documentElement.classList.remove('dark');
  }, []);

  const value = {
    // Sidebar
    sidebarOpen,
    isMobile,
    toggleSidebar,
    openSidebar,
    closeSidebar,

    // Theme
    theme,
    isDarkMode: theme === 'dark',
    toggleTheme,
    setLightTheme,
    setDarkTheme,

    // Layout
    layoutDensity,
    changeLayoutDensity,

    // Notifications
    notificationsEnabled,
    soundEnabled,
    toggleNotifications,
    toggleSound,

    // Reset
    resetPreferences,
  };

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};

export default UIContext;